import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  CalendarClock,
  ClipboardList,
  Users as UsersIcon,
  CalendarOff,
  UserCircle,
  LogOut,
  Menu,
} from "lucide-react";

import { Button } from "../../components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "../../components/ui/sheet";

import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import NotificationBell from "../../components/NotificationBell";

const NAV = [
  {
    to: "/doctor",
    label: "Today",
    icon: CalendarClock,
    end: true,
    testid: "doc-nav-today",
  },
  {
    to: "/doctor/appointments",
    label: "Appointments",
    icon: ClipboardList,
    testid: "doc-nav-appts",
  },
  {
    to: "/doctor/patients",
    label: "Patients",
    icon: UsersIcon,
    testid: "doc-nav-patients",
  },
  {
    to: "/doctor/blocks",
    label: "Time off",
    icon: CalendarOff,
    testid: "doc-nav-blocks",
  },
  {
    to: "/doctor/profile",
    label: "Profile",
    icon: UserCircle,
    testid: "doc-nav-profile",
  },
];

export default function DoctorLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    api
      .get("/doctor/me")
      .then((response) => setMe(response.data))
      .catch(() => {});
  }, []);

  const onLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const NavList = ({ onClick }) => (
    <nav className="flex items-center gap-1 sm:gap-2">
      {NAV.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={onClick}
          data-testid={item.testid}
          className={({ isActive }) =>
            `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
              isActive
                ? "bg-teal-500/10 text-teal-300"
                : "text-mb-fg2 hover:bg-mb-surface2 hover:text-white"
            }`
          }
        >
          <item.icon className="h-4 w-4" />
          <span className="hidden lg:inline">{item.label}</span>
        </NavLink>
      ))}
    </nav>
  );

  const hospitalName = me?.hospital?.name || "MediBooks";
  const hospitalLogo = me?.hospital?.logo_url;

  return (
    <div className="min-h-screen bg-mb-bg text-white">
      <header className="sticky top-0 z-10 flex h-16 items-center gap-6 border-b border-mb-border bg-mb-bg/95 px-5 backdrop-blur lg:px-10">
        <NavLink
          to="/doctor"
          aria-label="Go to doctor dashboard"
          data-testid="doctor-brand"
          className="flex shrink-0 items-center gap-3 rounded-lg transition-opacity hover:opacity-90"
        >
          {hospitalLogo ? (
            <img
              src={hospitalLogo}
              alt={`${hospitalName} logo`}
              className="h-10 w-10 shrink-0 rounded-xl bg-white/5 object-contain p-1"
            />
          ) : (
            <img
              src="/medibooks-logo.png"
              alt="MediBooks"
              className="h-10 w-10 shrink-0 rounded-xl bg-white object-contain p-1"
            />
          )}

          <div className="hidden min-w-0 sm:block">
            <div className="max-w-[180px] truncate text-sm font-semibold">
              {hospitalName}
            </div>

            <div className="text-[10px] uppercase tracking-[0.25em] text-mb-fg2">
              {me?.specialty ? `${me.specialty} Doctor` : "Doctor Portal"}
            </div>
          </div>
        </NavLink>

        <div className="hidden flex-1 items-center md:flex">
          <NavList />
        </div>

        <div className="flex-1 md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="ghost"
                data-testid="doc-mobile-menu"
                className="h-9 w-9 p-0 hover:bg-mb-surface2"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>

            <SheetContent
              side="left"
              className="w-72 border-mb-border bg-mb-bg p-5 text-white"
            >
              <div className="mb-5">
                <NavLink
                  to="/doctor"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3"
                >
                  {hospitalLogo ? (
                    <img
                      src={hospitalLogo}
                      alt={`${hospitalName} logo`}
                      className="h-10 w-10 rounded-xl bg-white/5 object-contain p-1"
                    />
                  ) : (
                    <img
                      src="/medibooks-logo.png"
                      alt="MediBooks"
                      className="h-10 w-10 rounded-xl bg-white object-contain p-1"
                    />
                  )}

                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold">
                      {hospitalName}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-mb-fg2">
                      Doctor Portal
                    </div>
                  </div>
                </NavLink>
              </div>

              <div className="mb-4 text-xs uppercase tracking-[0.25em] text-mb-fg2">
                Menu
              </div>

              <div className="flex flex-col gap-2">
                <NavList onClick={() => setOpen(false)} />
              </div>
            </SheetContent>
          </Sheet>
        </div>

        <div className="flex items-center gap-2">
          <NotificationBell testid="doctor-notification-bell" />

          <div
            className="hidden flex-col items-end sm:flex"
            data-testid="doctor-name"
          >
            <span className="text-xs text-mb-fg2">
              {user?.full_name}
            </span>

            <span className="text-[10px] uppercase tracking-[0.2em] text-mb-fg2">
              {hospitalName}
            </span>
          </div>

          <Button
            onClick={onLogout}
            variant="ghost"
            data-testid="doctor-logout"
            className="h-9 px-2 hover:bg-mb-surface2"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-8 lg:px-10">
        <Outlet context={{ me }} />
      </main>
    </div>
  );
}